import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Calendar,
  Clock,
  ExternalLink,
  TrendingUp,
  Filter,
  Search,
  Newspaper,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { fetchAgronegocioNews } from '../services/newsApi';

const Noticias = () => {
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const categorias = [
    { id: 'todas', nome: 'Todas', cor: 'bg-gray-500' },
    { id: 'leiloes', nome: 'Leilões', cor: 'bg-[rgb(25,42,86)]' },
    { id: 'mercado', nome: 'Mercado', cor: 'bg-green-600' },
    { id: 'tecnologia', nome: 'Tecnologia', cor: 'bg-blue-600' },
    { id: 'agricultura', nome: 'Agricultura', cor: 'bg-yellow-600' },
    { id: 'suinos', nome: 'Suínos', cor: 'bg-pink-600' },
    { id: 'leiloes', nome: 'Leilões', cor: 'bg-[rgb(238,29,37)]' },
    { id: 'sustentabilidade', nome: 'Sustentabilidade', cor: 'bg-emerald-600' },
    { id: 'economia', nome: 'Economia', cor: 'bg-orange-600' }
  ];

  useEffect(() => {
    carregarNoticias();
  }, []);

  const carregarNoticias = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchAgronegocioNews();
      
      if (response.success && response.noticias) {
        setNoticias(response.noticias);
      } else {
        setError('Nenhuma notícia encontrada');
      }
    } catch (err) {
      console.error('Erro ao carregar notícias:', err);
      setError('Erro ao carregar notícias. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    const dataObj = new Date(data);
    return dataObj.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const getCategoriaInfo = (categoria) => {
    return categorias.find(cat => cat.id === categoria) || categorias[0];
  };

  const noticiasFiltradas = noticias.filter(noticia => {
    const matchCategoria = filtroCategoria === 'todas' || noticia.categoria === filtroCategoria;
    const matchSearch = noticia.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       noticia.resumo.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategoria && matchSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Newspaper className="h-12 w-12 mr-4 text-[rgb(25,42,86)]" />
                <h1 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)]">
                  Notícias do Agronegócio
                </h1>
              </div>
              <p className="text-xl opacity-80 font-semibold">
                Carregando as últimas notícias do agronegócio...
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <RefreshCw className="h-6 w-6 animate-spin text-[rgb(46,48,146)]" />
              <span className="text-lg">Buscando notícias em tempo real...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Newspaper className="h-12 w-12 mr-4 text-[rgb(25,42,86)]" />
                <h1 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)]">
                  Notícias do Agronegócio
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-gray-900">Erro ao Carregar Notícias</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button
              onClick={carregarNoticias}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(35,52,96)] text-white font-bold"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Newspaper className="h-12 w-12 mr-4 text-[rgb(25,42,86)]" />
              <h1 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)]">
                Notícias do Agronegócio
              </h1>
            </div>
            <p className="text-xl opacity-80 font-semibold">
              Fique por dentro das principais notícias do setor
            </p>
            
            {/* Estatísticas */}
            <div className="flex items-center justify-center mt-8 space-x-8">
              <div className="text-center">
                <div className="text-2xl font-black text-[rgb(25,42,86)]">{noticias.length}</div>
                <div className="text-sm opacity-75 font-semibold">Notícias</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-[rgb(25,42,86)]">{categorias.length - 1}</div>
                <div className="text-sm opacity-75 font-semibold">Categorias</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-[rgb(25,42,86)]">Hoje</div>
                <div className="text-sm opacity-75 font-semibold">Atualizado</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Busca */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar notícias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(46,48,146)] focus:border-transparent"
              />
            </div>

            {/* Filtros de Categoria */}
            <div className="flex items-center gap-2 overflow-x-auto">
              <Filter className="h-4 w-4 text-gray-600 flex-shrink-0" />
              <div className="flex gap-2">
                {categorias.map((categoria) => (
                  <Button
                    key={categoria.id}
                    variant={filtroCategoria === categoria.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFiltroCategoria(categoria.id)}
                    className={`whitespace-nowrap ${
                      filtroCategoria === categoria.id 
                        ? `${categoria.cor} hover:opacity-90 text-white`
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {categoria.nome}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Notícias */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {noticiasFiltradas.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nenhuma notícia encontrada</h3>
            <p className="text-gray-600 mb-6">
              Tente ajustar os filtros ou termo de busca.
            </p>
            
            <Button
              onClick={() => {
                setFiltroCategoria('todas');
                setSearchTerm('');
              }}
              variant="outline"
              className="border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Limpar Filtros
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {noticiasFiltradas.map((noticia) => {
              const categoriaInfo = getCategoriaInfo(noticia.categoria);
              
              return (
                <Card key={noticia.id} className="border-2 hover:border-[rgb(46,48,146)] transition-all duration-200 overflow-hidden">
                  {/* Imagem */}
                  <div className="relative h-40 overflow-hidden bg-gray-100">
                    <img
                      src={noticia.imagem}
                      alt={noticia.titulo}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = 'https://www.canalrural.com.br/wp-content/themes/seox-theme-publishers-child/assets/images/logo.png';
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className={`${categoriaInfo.cor} text-white hover:opacity-90`}>
                        {categoriaInfo.nome}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-lg leading-tight hover:text-[rgb(46,48,146)] transition-colors">
                      {noticia.titulo}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {noticia.resumo}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 font-semibold">{noticia.fonte || 'Canal Rural'}</span>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
                        onClick={() => {
                          if (noticia.link && noticia.link !== '#') {
                            window.open(noticia.link, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Ler Mais
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Resumo dos Resultados */}
        {noticiasFiltradas.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Mostrando {noticiasFiltradas.length} de {noticias.length} notícias
              {filtroCategoria !== 'todas' && (
                <span> • Categoria: <strong>{getCategoriaInfo(filtroCategoria).nome}</strong></span>
              )}
              {searchTerm && (
                <span> • Busca: <strong>"{searchTerm}"</strong></span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Rodapé da Página */}
      <div className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <TrendingUp className="h-8 w-8 text-[rgb(46,48,146)] mr-2" />
              <h3 className="text-lg font-semibold">Mantenha-se Atualizado</h3>
            </div>
            <p className="text-gray-600 mb-4">
              As notícias são atualizadas constantemente para manter você informado sobre o agronegócio brasileiro.
            </p>
            <Button
              onClick={carregarNoticias}
              variant="outline"
              className="border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar Notícias
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Noticias;